/**
 * Ticket Redemption Component for Kiosk
 *
 * Allows guests with meal tickets to scan/enter their ticket code.
 * Validates tickets and shows friendly messages for already-used tickets.
 */

import { useState } from 'react';
import { useValidateTicket } from '../../hooks/useTickets';
import { QRScanner } from './QRScanner';

interface TicketRedemptionProps {
  onRedemptionComplete: (guestName: string, ticketCode: string, mealType?: string) => void;
}

export function TicketRedemption({ onRedemptionComplete }: TicketRedemptionProps) {
  const [ticketCode, setTicketCode] = useState('HMH-');
  const [guestName, setGuestName] = useState('');
  const [ticketMealType, setTicketMealType] = useState<string | undefined>(undefined);
  const [step, setStep] = useState<'scan' | 'name' | 'already-used'>('scan');
  const [errorMessage, setErrorMessage] = useState('');
  const [redeemedInfo, setRedeemedInfo] = useState<{ date: string; name?: string } | null>(null);
  const [showScanner, setShowScanner] = useState(false);

  const validateTicket = useValidateTicket();

  // Handle QR code scan
  const handleQRScan = async (code: string) => {
    setTicketCode(code.toUpperCase());
    setShowScanner(false);
    // Auto-validate when scanned
    try {
      const result = await validateTicket.mutateAsync(code.trim().toUpperCase());
      if (result.valid) {
        setTicketMealType(result.meal_type);
        setStep('name');
      } else {
        if (result.message?.includes('already redeemed')) {
          setRedeemedInfo({
            date: result.message.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/)?.[0] || 'previously',
            name: result.redeemed_by
          });
          setStep('already-used');
        } else {
          setErrorMessage(result.message || 'Invalid ticket');
        }
      }
    } catch (error: any) {
      const errorText = error.message || 'Failed to validate ticket';
      if (errorText.includes('already redeemed')) {
        setStep('already-used');
      } else {
        setErrorMessage(errorText);
      }
    }
  };

  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!ticketCode.trim()) {
      setErrorMessage('Please enter a ticket code');
      return;
    }

    try {
      const result = await validateTicket.mutateAsync(ticketCode.trim().toUpperCase());

      if (result.valid) {
        // Valid ticket - proceed to name entry
        setTicketMealType(result.meal_type);
        setStep('name');
      } else {
        // Check if ticket was already redeemed
        if (result.message?.includes('already redeemed')) {
          setRedeemedInfo({
            date: result.message.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/)?.[0] || 'previously',
            name: result.redeemed_by
          });
          setStep('already-used');
        } else {
          // Other validation errors (expired, voided, etc.)
          setErrorMessage(result.message || 'Invalid ticket');
        }
      }
    } catch (error: any) {
      // Handle network or other errors
      const errorText = error.message || 'Failed to validate ticket';
      if (errorText.includes('already redeemed')) {
        setStep('already-used');
      } else {
        setErrorMessage(errorText);
      }
    }
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (guestName.trim()) {
      onRedemptionComplete(guestName.trim(), ticketCode.trim().toUpperCase(), ticketMealType);
    }
  };

  const handleTryAgain = () => {
    setTicketCode('HMH-');
    setGuestName('');
    setTicketMealType(undefined);
    setStep('scan');
    setErrorMessage('');
    setRedeemedInfo(null);
    setShowScanner(false);
  };

  // Already Used Screen
  if (step === 'already-used') {
    return (
      <div className="space-y-6">
        {/* Friendly message box */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 text-center">
          <div className="w-16 h-16 bg-amber-200 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          <h3 className="text-2xl font-bold text-amber-900 mb-2">
            Ticket Already Used
          </h3>

          <p className="text-lg text-amber-800 mb-1">
            This meal ticket has already been redeemed.
          </p>

          {redeemedInfo && (
            <div className="text-base text-amber-700 mt-3">
              {redeemedInfo.name && (
                <p>Used by: <span className="font-semibold">{redeemedInfo.name}</span></p>
              )}
              {redeemedInfo.date !== 'previously' && (
                <p className="text-sm mt-1">On: {redeemedInfo.date}</p>
              )}
            </div>
          )}
        </div>

        {/* Ticket code display */}
        <div className="bg-gray-100 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-600 mb-1">Ticket Code</p>
          <p className="text-xl font-mono font-bold text-gray-900">{ticketCode}</p>
        </div>

        {/* Action button */}
        <button
          onClick={handleTryAgain}
          className="w-full py-4 bg-[#1e62ac] hover:bg-[#10348e] text-white text-lg font-semibold rounded-xl transition-colors"
        >
          Try a Different Ticket
        </button>

        <div className="text-gray-500 text-sm text-center">
          Please check with staff if you believe this is an error.
        </div>
      </div>
    );
  }

  // Name Entry Screen
  if (step === 'name') {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-green-700">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-semibold">Valid Ticket</span>
          </div>
          <p className="text-sm text-green-600 mt-1 font-mono">{ticketCode}</p>
        </div>

        <form onSubmit={handleNameSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please enter your name
            </label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Your name..."
              className="w-full px-4 py-4 text-lg bg-white border-2 border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#1e62ac] transition-colors"
              autoComplete="off"
              autoFocus
              required
            />
          </div>

          <button
            type="submit"
            disabled={!guestName.trim()}
            className="w-full py-4 bg-[#1e62ac] hover:bg-[#10348e] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-lg font-semibold rounded-xl transition-colors"
          >
            Continue
          </button>

          <button
            type="button"
            onClick={handleTryAgain}
            className="w-full py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 text-base font-medium rounded-xl transition-colors"
          >
            Use Different Ticket
          </button>
        </form>
      </div>
    );
  }

  // Ticket Scan/Entry Screen
  return (
    <div className="space-y-4">
      {/* QR Scanner */}
      {showScanner && (
        <div className="mb-4">
          <QRScanner
            onScan={handleQRScan}
            onError={() => setShowScanner(false)}
          />
        </div>
      )}

      {/* Toggle between scanner and manual entry */}
      {showScanner ? (
        <button
          type="button"
          onClick={() => setShowScanner(false)}
          className="w-full py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 text-base font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Close Camera
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setShowScanner(true)}
          className="w-full py-3 bg-[#1e62ac] hover:bg-[#10348e] text-white text-base font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Scan QR Code
        </button>
      )}

      {/* Manual Entry Form */}
      {!showScanner && (
        <form onSubmit={handleTicketSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              value={ticketCode}
              onChange={(e) => {
                let val = e.target.value.toUpperCase();
                // Ensure HMH- prefix is always present
                if (!val.startsWith('HMH-')) {
                  val = 'HMH-' + val.replace(/^HMH-?/i, '');
                }
                setTicketCode(val);
              }}
              placeholder="HMH-XXXXXX"
              className="w-full px-4 py-4 text-lg bg-white border-2 border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#1e62ac] transition-colors font-mono tracking-wide"
              autoComplete="off"
              autoFocus
            />
            <p className="text-sm text-gray-500 mt-2">
              Enter the 6-character code after HMH-
            </p>
          </div>

          <button
            type="submit"
            disabled={!ticketCode.trim() || validateTicket.isPending}
            className="w-full py-4 bg-[#1e62ac] hover:bg-[#10348e] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-lg font-semibold rounded-xl transition-colors"
          >
            {validateTicket.isPending ? 'Validating...' : 'Validate Ticket'}
          </button>
        </form>
      )}

      {errorMessage && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-red-700 text-center">
          <p className="font-medium">{errorMessage}</p>
        </div>
      )}

      <div className="text-gray-500 text-sm text-center">
        Guests with meal tickets can use this option
      </div>
    </div>
  );
}
